/** Format UTC ISO dates from API for display. All formatters validate with parseISO + isValid. */

import { parseISO, isValid, formatDistanceToNow } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { logger } from '@/lib/logger'
import { DATE_FORMATS } from './constants'
import { getUserTimezone } from './timezone'

export function formatEventDate(
  utcDate: string,
  eventTimezone: string,
  formatPattern: string = DATE_FORMATS.MEDIUM_WITH_TZ
): string {
  try {
    const date = parseISO(utcDate)
    if (!isValid(date)) return 'Invalid date'
    return formatInTimeZone(date, eventTimezone, formatPattern)
  } catch (error) {
    logger.error('Error formatting date:', error)
    return 'Invalid date'
  }
}

export function formatInUserTimezone(
  utcDate: string,
  formatPattern: string = DATE_FORMATS.MEDIUM
): string {
  try {
    const date = parseISO(utcDate)
    if (!isValid(date)) return 'Invalid date'
    return formatInTimeZone(date, getUserTimezone(), formatPattern)
  } catch (error) {
    logger.error('Error formatting date:', error)
    return 'Invalid date'
  }
}

/** Relative time via date-fns formatDistanceToNow (e.g. in 2 days, 3 hours ago). */
export function getRelativeTime(utcDate: string): string {
  try {
    const date = parseISO(utcDate)
    if (!isValid(date)) return 'Invalid date'
    return formatDistanceToNow(date, { addSuffix: true })
  } catch (error) {
    logger.error('Error calculating relative time:', error)
    return 'Invalid date'
  }
}

export function formatRelativeTime(timestamp: string): string {
  try {
    const date = parseISO(timestamp)
    if (!isValid(date)) return 'Invalid date'
    return formatDistanceToNow(date, { addSuffix: true })
  } catch (error) {
    logger.error('Error formatting relative time:', error)
    return 'Invalid date'
  }
}

export function formatDateShort(
  utcDate?: string | null,
  eventTimezone?: string
): string {
  if (!utcDate) return 'TBD'

  try {
    const date = parseISO(utcDate)
    if (!isValid(date)) return 'Invalid date'
    const timezone = eventTimezone || getUserTimezone()
    return formatInTimeZone(date, timezone, DATE_FORMATS.DATE_MEDIUM)
  } catch (error) {
    logger.error('Error formatting short date:', error)
    return 'Invalid date'
  }
}

/** Duration in days → human string (e.g. 7 days 12 hrs). Epsilon in hour calc avoids float rounding down. */
export function formatDuration(days: number): string {
  const wholeDays = Math.floor(days)
  const remainingHours = Math.floor((days - wholeDays) * 24 + 1e-9)

  if (wholeDays === 0) {
    if (remainingHours === 1) return '1 hr'
    return `${remainingHours} hrs`
  }

  if (remainingHours === 0 || remainingHours === 24) {
    const totalDays = remainingHours === 24 ? wholeDays + 1 : wholeDays
    if (totalDays === 1) return '1 day'
    return `${totalDays} days`
  }

  const dayLabel = wholeDays === 1 ? 'day' : 'days'
  const hourLabel = remainingHours === 1 ? 'hr' : 'hrs'

  return `${wholeDays} ${dayLabel} ${remainingHours} ${hourLabel}`
}
